import {
  autoUpdate,
  FloatingPortal,
  flip,
  offset,
  type Placement,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import { memo, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { cn } from "src/utilities/shadcnUtils";

type Place =
  | "top"
  | "top-start"
  | "top-end"
  | "right"
  | "right-start"
  | "right-end"
  | "bottom"
  | "bottom-start"
  | "bottom-end"
  | "left"
  | "left-start"
  | "left-end";

export interface TooltipProps {
  children: ReactNode;
  text: string | ReactNode;
  className?: string;
  spanClassName?: string;
  white?: boolean;
  clickable?: boolean;
  delay?: number;
  place?: Place;
  complex?: boolean;
  enabled?: boolean;
}

const stopProp = (e: any) => e.stopPropagation?.();

/** Inner component that owns floating-ui hooks — only mounts after hasOpened. */
const TooltipContent = memo(function TooltipContent({
  text,
  white,
  clickable,
  open,
  setOpen,
  triggerRef,
  place,
  onContentMouseEnter,
  onContentMouseLeave,
  className,
}: {
  text: string | ReactNode;
  white?: boolean;
  clickable?: boolean;
  open: boolean;
  setOpen: (o: boolean) => void;
  triggerRef: React.RefObject<HTMLElement | null>;
  place?: Place;
  onContentMouseEnter?: () => void;
  onContentMouseLeave?: () => void;
  className?: string;
}) {
  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: (place as Placement) || "top",
    middleware: [offset(4), flip(), shift()],
    whileElementsMounted: autoUpdate,
  });

  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "tooltip" });
  const { getFloatingProps } = useInteractions([dismiss, role]);

  // Sync trigger ref as floating reference
  useEffect(() => {
    if (triggerRef.current) refs.setReference(triggerRef.current);
  }, [triggerRef, refs]);

  // `max-w-md` is a default, not a cap: `cn` is tailwind-merge, and the caller's `className` is
  // applied after this, so a tooltip that genuinely needs to be wider (or `max-w-none`) still wins.
  // Without it a tooltip holding a sentence or two stretches to the full width of the viewport.
  const contentClassName = useMemo(
    () =>
      white
        ? "bg-card text-foreground text-xs px-3 py-2 border border-input z-[1100] max-w-md overflow-hidden rounded-sm animate-in fade-in-0 zoom-in-95 pointer-events-auto"
        : "border border-input z-[1100] max-w-md overflow-hidden rounded-sm bg-card animate-in fade-in-0 zoom-in-95 pointer-events-auto",
    [white]
  );

  if (!open) return null;

  return (
    <FloatingPortal>
      <div
        ref={refs.setFloating}
        style={floatingStyles}
        className={cn(contentClassName, className)}
        onClick={clickable ? stopProp : undefined}
        onMouseEnter={onContentMouseEnter}
        onMouseLeave={onContentMouseLeave}
        {...getFloatingProps()}
      >
        {text}
      </div>
    </FloatingPortal>
  );
});

const Tooltip = memo(function Tooltip({
  children,
  text,
  white = true,
  clickable,
  delay = 300,
  place,
  enabled = true,
  className,
  spanClassName,
}: TooltipProps) {
  const [open, setOpenState] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const triggerRef = useRef<HTMLElement | null>(null);
  const openTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const hasOpenedRef = useRef(false);

  useEffect(() => {
    return () => {
      clearTimeout(openTimerRef.current);
      clearTimeout(closeTimerRef.current);
    };
  }, []);

  const setOpen = useCallback((o: boolean) => {
    setOpenState(o);
    if (o && !hasOpenedRef.current) {
      hasOpenedRef.current = true;
      setHasOpened(true);
    }
  }, []);

  const handleMouseEnter = useCallback(() => {
    clearTimeout(closeTimerRef.current);
    openTimerRef.current = setTimeout(() => setOpen(true), delay);
  }, [setOpen, delay]);

  const handleMouseLeave = useCallback(() => {
    clearTimeout(openTimerRef.current);
    if (clickable) {
      closeTimerRef.current = setTimeout(() => setOpen(false), 150);
    } else {
      setOpen(false);
    }
  }, [setOpen, clickable]);

  const handleContentMouseEnter = useCallback(() => {
    clearTimeout(closeTimerRef.current);
  }, []);

  const handleContentMouseLeave = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  if (!enabled) {
    return <span>{children}</span>;
  }

  return (
    <>
      <span
        className={cn("flex", spanClassName)}
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </span>
      {hasOpened && text ? (
        <TooltipContent
          className={className}
          text={text}
          white={white}
          clickable={clickable}
          open={open}
          setOpen={setOpen}
          triggerRef={triggerRef}
          place={place}
          onContentMouseEnter={clickable ? handleContentMouseEnter : undefined}
          onContentMouseLeave={clickable ? handleContentMouseLeave : undefined}
        />
      ) : null}
    </>
  );
});

export default Tooltip;
